import React, { useRef, useState, useEffect } from "react";
import logo from "../../assets/creedom_logo.png"; // Update path
import {
  FaArrowLeft,
  FaShareAlt,
  FaVolumeMute,
  FaVolumeUp,
  FaTimes,
} from "react-icons/fa";
import SubscriptionLogin from "./SubscriptionLogin";
import { useNavigate } from "react-router-dom";
import Hls from "hls.js";
import { useParams } from "react-router-dom";
import LearnService from "../../services/LearnService";

const SubscriptionLanding = () => {
  const { processId } = useParams();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(() => {
    // If coming from learn screen, do not mute
    if (sessionStorage.getItem('from_learn') === '1') {
      sessionStorage.removeItem('from_learn');
      return false;
    }
    // If direct link, mute initially
    return true;
  });
  const [showSubscribeOverlay, setShowSubscribeOverlay] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const navigate = useNavigate();
  const [filling, setFilling] = useState(false);
  const [fillPercent, setFillPercent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [episodeData, setEpisodeData] = useState(null);
  const [thumbUrl, setThumbUrl] = useState("");
  const [storyUrl, setStoryUrl] = useState("");
  const [showTapToMute, setShowTapToMute] = useState(true); // Show label initially
  const [hasUserUnmuted, setHasUserUnmuted] = useState(false); // Track if user unmuted
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);

  const m3u8Url = storyUrl;
  // const m3u8Url =
  //   "https://tecxprt-media.sgp1.digitaloceanspaces.com/peertube-stream/hls/0a5404a8-7824-4220-88e6-1ee9751ebef3/3944586f-d2eb-4dd9-ab93-45191da4a2cb-master.m3u8";

  // useEffect(() => {
  //   if (loading) return;
  //   if (!videoRef.current || !storyUrl ) return;

  //   const video = videoRef.current;
  //   const videoUrl = storyUrl;
  //   console.log("storyUrl", storyUrl)
  //   if (Hls.isSupported()) {
  //     const hls = new Hls();
  //     hls.loadSource(videoUrl);
  //     hls.attachMedia(video);
  //     hls.on(Hls.Events.MANIFEST_PARSED, function () {
  //       video.muted = false; // Ensure not muted
  //       video?.play()?.catch((e) => {
  //         console.log('Autoplay prevented:', e);
  //       });
  //     });
  //     return () => {
  //       hls.destroy();
  //     };
  //   } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
  //     video.src = videoUrl;
  //     video.muted = false; // Ensure not muted
  //     video.play().catch((e) => {
  //       console.log('Autoplay prevented:', e);
  //     });
  //   }
  // }, [storyUrl, loading]);

  useEffect(() => {
    if (!m3u8Url || !videoRef.current) return;
    let hls;
    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(m3u8Url);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, function () {
        setShouldAutoPlay(true); // Only set flag, do not call play() here
      });
    } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
      videoRef.current.src = m3u8Url;
      setShouldAutoPlay(true); // Only set flag, do not call play() here
    }
    return () => {
      if (hls) hls.destroy();
    };
  }, [m3u8Url, isVideoPlaying]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await LearnService.getItemDetail(processId);
        const data = res.data?.[0] || null;
        setEpisodeData(data);

        if (data?.taskMetaData?.media) {
          const thumb = data.taskMetaData.media.find(
            (m) => m.type === "thumbNail"
          );
          const story = data.taskMetaData.media.find((m) => m.type === "story");
          setThumbUrl(thumb?.mediaUrl || "");
          setStoryUrl(story?.mediaUrl || "");
        }
      } catch (e) {
        console.log("Error: Item Detail", e);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  
  useEffect(() => {
    if (videoRef.current) {
      if (showSubscribeOverlay) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else if (!showSubscribeOverlay && isPlaying) {
        videoRef.current.play();
      }
    }
  }, [showSubscribeOverlay]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
    }
  }, [muted]);

  // On initial load, mute for 5 seconds, then auto-unmute if user hasn't unmuted
  useEffect(() => {
    if (!hasUserUnmuted && muted) {
      setShowTapToMute(true);
      const timer = setTimeout(() => {
        setMuted(false);
        setShowTapToMute(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [muted, hasUserUnmuted]);

  // Hide the label after 3 seconds if not tapped (remove this effect, replaced by above)

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current.duration);
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  // Animate fillPercent when overlay is shown
  useEffect(() => {
    let animationFrame;
    if (showSubscribeOverlay) {
      setFillPercent(0);
      let start;
      const duration = 3000; // 3 seconds for faster fill
      const animate = (timestamp) => {
        if (!start) start = timestamp;
        const elapsed = timestamp - start;
        const t = Math.min(elapsed / duration, 1);
        const percent = t * 100;
        setFillPercent(percent);
        if (percent < 100) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          // On fill complete, trigger navigation
          handleNextEpisodeClick();
        }
      };
      animationFrame = requestAnimationFrame(animate);
    } else {
      setFillPercent(0);
    }
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [showSubscribeOverlay]);

  // Auto-navigate after fill completes
  useEffect(() => {
    if (showSubscribeOverlay && fillPercent >= 100) {
      // Navigation logic (same as handleNextEpisodeClick)
      const accessToken = localStorage.getItem("access_token");
      const userDetail = JSON.parse(
        localStorage.getItem("user_detail") || "{}"
      );
      const profileDetails = JSON.parse(
        localStorage.getItem("profileDetail") || "{}"
      );

      if (
        accessToken &&
        (userDetail?.user?.membership === "PRO" ||
          profileDetails?.userId?.membership === "PRO")
      ) {
        navigate("/subscribe-screen");
        return;
      }
      if (
        accessToken &&
        userDetail?.user?.membership !== "PRO" &&
        profileDetails?.userId?.membership !== "PRO"
      ) {
        navigate(`/learn/paywall/${processId}`);
        return;
      }
      navigate(`/learn/login/${processId}`);
    }
  }, [fillPercent, showSubscribeOverlay, navigate]);

  const handleNextEpisodeClick = () => {
    setFilling(true);
    setTimeout(() => {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        navigate(`/learn/paywall/${processId}`);
        return;
      }
      const userDetail = JSON.parse(
        localStorage.getItem("user_detail") || "{}"
      );
      const profileDetails = JSON.parse(
        localStorage.getItem("profileDetail") || "{}"
      );
      if (
        userDetail?.user?.membership === "PRO" ||
        profileDetails?.userId?.membership === "PRO"
      ) {
        navigate("/subscribe-screen");
        return;
      }
      navigate(`/learn/paywall/${processId}`);
    }, 500); // match the CSS transition
  };

  useEffect(() => {
    if (showSubscribeOverlay) {
      setFilling(true);
      const timeout = setTimeout(() => {
        // Navigation logic here
        // Example:
        // navigate("/subscribe-screen");
      }, 500); // match the CSS transition
      return () => clearTimeout(timeout);
    } else {
      setFilling(false);
    }
  }, [showSubscribeOverlay]);

  return (
    <div
      style={{
        background: "#000",
        minHeight: "100dvh",
        height: "100dvh",
        width: "100vw",
        maxWidth: "100vw",
        padding: 0,
        margin: 0,
        position: "relative",
        top: 0,
        left: 0,
        zIndex: 9999,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#000a",
          color: "#fff",
          borderRadius: 0,
          padding: 12,
          zIndex: 2,
        }}
      >
        <img src={logo} alt="Logo" style={{ height: 32 }} />
        {/* <FaShareAlt style={{ cursor: "pointer" }} /> */}
      </div>

      {/* Video Player */}
      <div
        style={{
          position: "relative",
          width: "100vw",
          height: "100dvh",
          maxWidth: "100vw",
          overflow: "hidden",
        }}
      >
        <video
          ref={videoRef}
          style={{
            width: "100vw",
            height: "100dvh",
          }}
          muted={muted}
          autoPlay
          loop={false}
          onEnded={() => setShowSubscribeOverlay(true)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={() => {
            setDuration(videoRef.current.duration);
            if (shouldAutoPlay && videoRef.current.paused) {
              setTimeout(() => {
                if (videoRef.current && videoRef.current.paused) {
                  videoRef.current.play().catch((e) => {
                    console.log("Autoplay prevented:", e);
                  });
                }
                setShouldAutoPlay(false); // Only try once
              }, 0); // Defer to next tick to ensure ready
            }
          }}
          src={storyUrl || undefined}
        />
        {/* Mute/Unmute Button (top-left) */}
        <div
          style={{
            position: "absolute",
            top: 70,
            left: 17,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
          }}
        >
          <button
            onClick={() => {
              setMuted(false);
              setShowTapToMute(false);
              setHasUserUnmuted(true);
            }}
            style={{
              background: "rgba(0,0,0,0.6)",
              border: "none",
              borderRadius: "50%",
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              cursor: "pointer",
            }}
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
          </button>
          {muted && showTapToMute && !hasUserUnmuted && (
            <span
              style={{
                marginLeft: 10,
                background: "rgba(0,0,0,0.7)",
                color: "#fff",
                padding: "6px 14px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 8px #0003",
                letterSpacing: 0.5,
              }}
            >
              Tap to unmute
            </span>
          )}
        </div>
        {/* Overlay Title */}
        {/* <div
          style={{
            position: "absolute",
            bottom: 120,
            left: 0,
            width: "100vw",
            textAlign: "center",
            color: "#fff",
            fontWeight: "bold",
            fontSize: 28,
            textShadow: "0 2px 8px #000",
            zIndex: 2,
          }}
        >
          MEHENGA CAMERA NAHI
        </div> */}
        {/* Course Info Overlay */}
        {/* Custom Controls */}
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: 0,
            width: "100%",
            display: "flex",
            alignItems: "center",
            zIndex: 3,
            padding: "0 24px",
          }}
        >
          {/* Play/Pause Button */}
          <button
            onClick={handlePlayPause}
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: "1px solid #FFF",
              background: "#000a",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 12,
              fontSize: 24,
              outline: "none",
              cursor: "pointer",
            }}
          >
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24">
                <rect x="5" y="4" width="4" height="16" fill="white" />
                <rect x="15" y="4" width="4" height="16" fill="white" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24">
                <polygon points="5,3 19,12 5,21" fill="white" />
              </svg>
            )}
          </button>
          {/* Seek Bar */}
          <input
            type="range"
            min={0}
            max={duration}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            style={{
              flex: 1,
              accentColor: "#FFF",
              height: 4,
              borderRadius: 2,
              background: "#FFF",
            }}
          />
        </div>
      </div>

      {showSubscribeOverlay && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.85)",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => setShowSubscribeOverlay(false)}
            style={{
              position: "absolute",
              top: 32,
              right: 32,
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 20,
              cursor: "pointer",
              zIndex: 10001,
            }}
            aria-label="Close"
          >
            <FaTimes />
          </button>
          {/* Card Image */}
          <img
            src={thumbUrl}
            alt={episodeData?.title || "Episode Thumbnail"}
            style={{
              width: 220,
              borderRadius: 16,
              marginBottom: 16,
              boxShadow: "0 4px 24px #0008",
            }}
          />
          {/* Episode Title */}
          <p
            className="ct-Text-semi-24 ct-text-light"
            style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 20,
              marginBottom: 24,
              textAlign: "center",
              maxWidth: 300,
              textShadow: "0 2px 8px #000",
            }}
          >
            {episodeData?.title}
          </p>
          {/* Subscribe To Watch Button */}
          <button
            onClick={handleNextEpisodeClick}
            style={{
              position: "relative",
              width: 220,
              height: 56,
              borderRadius: 5,
              border: "2px solid #222",
              background: "transparent",
              color: "#222",
              fontWeight: 700,
              fontSize: 18,
              cursor: "pointer",
              overflow: "hidden",
              boxShadow: "0 2px 12px #0005",
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderColor: "#222",
            }}
          >
            {/* Fill animation overlay */}
            <span
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: `${fillPercent}%`,
                background: "#fff",
                zIndex: 1,
                transition: "none",
                pointerEvents: "none",
              }}
            />
            <span
              style={{
                position: "relative",
                zIndex: 2,
                opacity: 1,
                color: "#222",
                textShadow: "none",
                fontFamily: "Gilroy-SemiBold, sans-serif",
              }}
            >
              Next Episode
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionLanding;
